/**
 * Security Analytics Utility Functions
 * Statistical analysis and threat scoring for security monitoring
 */

/**
 * Calculate mean (average) of an array of numbers
 */
export function calculateMean(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
}

/**
 * Calculate standard deviation of an array of numbers
 */
export function calculateStdDev(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = calculateMean(values);
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = calculateMean(squaredDiffs);
  return Math.sqrt(variance);
}

/**
 * Calculate Z-score for anomaly detection
 * Z-score > 2 indicates anomaly (95% confidence)
 * Z-score > 3 indicates strong anomaly (99.7% confidence)
 */
export function calculateZScore(value: number, mean: number, stdDev: number): number {
  if (stdDev === 0) return 0;
  return (value - mean) / stdDev;
}

/**
 * Calculate moving average for smoothing time-series data
 */
export function calculateMovingAverage(data: number[], windowSize: number): number[] {
  if (data.length === 0) return [];

  const result: number[] = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const window = data.slice(start, i + 1);
    const avg = calculateMean(window);
    result.push(avg);
  }
  return result;
}

/**
 * Detect peaks in time-series data
 * Returns indices of detected peaks
 */
export function detectPeaks(data: number[], threshold: number = 1.5): number[] {
  if (data.length < 3) return [];

  const peaks: number[] = [];
  const ma = calculateMovingAverage(data, 10);

  for (let i = 1; i < data.length - 1; i++) {
    const isPeak = data[i] > data[i - 1] && data[i] > data[i + 1];
    const aboveBaseline = data[i] > ma[i] * threshold;

    if (isPeak && aboveBaseline) {
      peaks.push(i);
    }
  }
  return peaks;
}

/**
 * Calculate percentile value from sorted data
 */
export function calculatePercentile(sortedValues: number[], percentile: number): number {
  if (sortedValues.length === 0) return 0;
  if (percentile <= 0) return sortedValues[0];
  if (percentile >= 100) return sortedValues[sortedValues.length - 1];

  const index = (percentile / 100) * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (lower === upper) {
    return sortedValues[lower];
  }

  return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
}

/**
 * Threat Score Component Interface
 *
 * Every component is optional: a component is folded into the score only when
 * it is actually supplied, so the score re-normalises over the weights that
 * are currently wired up. Previously the unwired components were passed as a
 * benign 0, which capped the score at blockRate's 30% and made the High and
 * Critical bands unreachable.
 *
 * resourceImpact (raw CPU/memory) was intentionally removed: high utilisation
 * is not a threat signal on its own and would inflate the score during
 * legitimate traffic peaks. Surface resource usage in Fleet Health instead.
 */
export interface ThreatComponents {
  blockRate?: number;         // 0-100: Percentage-based score
  connectionAnomaly?: number; // 0-100: Connection pattern anomaly score
  ipDiversity?: number;       // 0-100: Unusual IP diversity score
  alertFrequency?: number;    // 0-100: Alert rate score
}

/**
 * Relative weight of each threat component. The score is a weighted average
 * over only the components supplied, so wiring up a new signal is just a
 * matter of passing it in — the denominator adjusts automatically.
 */
const THREAT_WEIGHTS: Record<keyof ThreatComponents, number> = {
  blockRate: 0.30,
  connectionAnomaly: 0.25,
  ipDiversity: 0.20,
  alertFrequency: 0.10,
};

/**
 * Calculate overall threat score (0-100) from the supplied component scores.
 * Components left undefined are excluded from both the weighted sum and the
 * weight total, so the result always spans the full 0-100 range for whatever
 * signals are currently available.
 */
export function calculateThreatScore(components: ThreatComponents): number {
  let weightedSum = 0;
  let activeWeight = 0;
  (Object.keys(THREAT_WEIGHTS) as Array<keyof ThreatComponents>).forEach((key) => {
    const value = components[key];
    if (value !== undefined) {
      weightedSum += value * THREAT_WEIGHTS[key];
      activeWeight += THREAT_WEIGHTS[key];
    }
  });
  return activeWeight > 0 ? weightedSum / activeWeight : 0;
}

/**
 * Determine threat level based on score
 */
export type ThreatLevel = 'Low' | 'Medium' | 'High' | 'Critical';

export function determineThreatLevel(score: number): ThreatLevel {
  if (score >= 75) return 'Critical';
  if (score >= 50) return 'High';
  if (score >= 25) return 'Medium';
  return 'Low';
}

/**
 * Calculate block rate score (0-100)
 * Based on percentage of blocked vs inspected packets
 */
export function calculateBlockRateScore(blockRate: number): number {
  // blockRate is a percentage (0-100)
  // Non-linear scaling: small block rates get lower scores
  if (blockRate < 1) return blockRate * 5;        // 0-5
  if (blockRate < 5) return 5 + (blockRate - 1) * 10;  // 5-45
  if (blockRate < 10) return 45 + (blockRate - 5) * 8; // 45-85
  return Math.min(85 + (blockRate - 10) * 1.5, 100); // 85-100
}

/**
 * Security Posture Score
 *
 * Posture answers "how well-defended am I?" — a coverage measure, and is
 * deliberately distinct from the threat score's "how much hostile activity is
 * happening?". Higher is better; 100 means every edge has security enabled and
 * policies configured.
 *
 * Kept separate from the threat score so the two axes don't contaminate each
 * other: a high block rate (lots of hostile traffic) should not drag down a
 * posture that is, in fact, fully configured and doing its job.
 */
export interface PostureComponents {
  edgesProtected: number;    // edges with ACL / security plugin enabled
  edgesWithPolicies: number; // edges carrying at least one active policy
  totalEdges: number;
}

/**
 * Calculate posture score (0-100, higher is better).
 * Weighted coverage: security enabled (60%) + policies configured (40%).
 */
export function calculatePostureScore({
  edgesProtected,
  edgesWithPolicies,
  totalEdges,
}: PostureComponents): number {
  if (totalEdges <= 0) return 0;
  const enabledCoverage = Math.min(edgesProtected / totalEdges, 1);
  const policyCoverage = Math.min(edgesWithPolicies / totalEdges, 1);
  return Math.round((enabledCoverage * 0.6 + policyCoverage * 0.4) * 100);
}

/**
 * Calculate connection anomaly score (0-100)
 * Based on deviation from baseline
 */
export function calculateConnectionAnomalyScore(
  currentFlows: number,
  baselineFlows: number
): number {
  if (baselineFlows === 0) return 0;

  const ratio = currentFlows / baselineFlows;

  if (ratio < 1.5) return 0;            // Within normal range
  if (ratio < 2) return (ratio - 1.5) * 40;  // 0-20
  if (ratio < 3) return 20 + (ratio - 2) * 30; // 20-50
  if (ratio < 5) return 50 + (ratio - 3) * 20; // 50-90
  return Math.min(90 + (ratio - 5) * 5, 100);  // 90-100
}

/**
 * Calculate IP diversity anomaly score (0-100)
 * Based on unique source IPs compared to baseline
 */
export function calculateIPDiversityScore(
  currentUniqueIPs: number,
  baselineUniqueIPs: number
): number {
  if (baselineUniqueIPs === 0) return 0;

  const ratio = currentUniqueIPs / baselineUniqueIPs;

  if (ratio < 2) return 0;              // Within normal range
  if (ratio < 5) return (ratio - 2) * 20;    // 0-60
  if (ratio < 10) return 60 + (ratio - 5) * 6; // 60-90
  return Math.min(90 + (ratio - 10), 100);     // 90-100
}

/**
 * Calculate alert frequency score (0-100)
 * Based on number of alerts in time window
 */
export function calculateAlertFrequencyScore(
  alertCount: number,
  timeWindowHours: number
): number {
  const alertsPerHour = alertCount / timeWindowHours;

  if (alertsPerHour < 1) return alertsPerHour * 10;        // 0-10
  if (alertsPerHour < 5) return 10 + (alertsPerHour - 1) * 15; // 10-70
  if (alertsPerHour < 10) return 70 + (alertsPerHour - 5) * 5; // 70-95
  return Math.min(95 + (alertsPerHour - 10) * 0.5, 100);       // 95-100
}

/**
 * Anomaly Classification
 */
export interface AnomalyClassification {
  isAnomaly: boolean;
  severity: 'normal' | 'mild' | 'moderate' | 'severe';
  zScore: number;
  deviation: number; // Percentage deviation from mean
}

/**
 * Classify data point as anomaly using Z-score
 */
export function classifyAnomaly(
  value: number,
  historicalValues: number[]
): AnomalyClassification {
  if (historicalValues.length < 2) {
    return {
      isAnomaly: false,
      severity: 'normal',
      zScore: 0,
      deviation: 0,
    };
  }

  const mean = calculateMean(historicalValues);
  const stdDev = calculateStdDev(historicalValues);
  const zScore = calculateZScore(value, mean, stdDev);
  const absZScore = Math.abs(zScore);
  const deviation = mean !== 0 ? ((value - mean) / mean) * 100 : 0;

  let severity: 'normal' | 'mild' | 'moderate' | 'severe' = 'normal';
  let isAnomaly = false;

  if (absZScore >= 3) {
    severity = 'severe';
    isAnomaly = true;
  } else if (absZScore >= 2.5) {
    severity = 'moderate';
    isAnomaly = true;
  } else if (absZScore >= 2) {
    severity = 'mild';
    isAnomaly = true;
  }

  return {
    isAnomaly,
    severity,
    zScore,
    deviation,
  };
}

/**
 * Format large numbers for display
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }
  return num.toFixed(0);
}

/**
 * Format bytes to human-readable format
 */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 4) {
    return (bytes / 1024 ** 4).toFixed(2) + ' TB';
  }
  if (bytes >= 1024 ** 3) {
    return (bytes / 1024 ** 3).toFixed(2) + ' GB';
  }
  if (bytes >= 1024 ** 2) {
    return (bytes / 1024 ** 2).toFixed(2) + ' MB';
  }
  if (bytes >= 1024) {
    return (bytes / 1024).toFixed(2) + ' KB';
  }
  return bytes.toFixed(0) + ' B';
}
